// Editor estruturado de proposta/venda — corpo da edição dentro do ViewerProposta.
// Inclui PreviewAssinaturaNegocio, ServicoComboBox, SecaoToggle, Secao e RangePrazoVenda.
// Extraído de ComercialPropostas.tsx durante refactor topológico.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { InputNumber } from 'antd';
import { API_URL } from '../../config';
import RichTextEditor from '../RichTextEditor';
import RangePrazo, { somarDias, parsePrazoTexto } from '../../components/RangePrazo';
import { getToken, fmtR } from './utils';
import { PRAZO_PAGAMENTO_DEFAULT } from './types';
import { fmtDataBr } from '../../utils/dataFmt';
import { totalItensVenda } from '../../utils/totalItensVenda';
import { PROPS_DINHEIRO_BR } from '../../utils/dinheiroInput';
import { parcelaPorPercentual, parcelaPorValor } from './valoresProposta';
import {
  labelMini, thMini, tdMini, tdEditMini,
  inputSm, inputBase, lbl,
} from './styles';

// ─────────────────────────── EDITOR ESTRUTURADO ───────────────────────────
// `tipo` controla o conjunto de campos visíveis:
//   - 'proposta' (default): todos campos (objeto técnico, entregáveis, cronograma,
//     condições gerais com 3 sub-blocos, dados bancários, etc) — alinhado com
//     renderHtmlPropostaModerna.
//   - 'venda': campos específicos da OS — empresa contratada, prazo (3 datas),
//     forma de pagamento, informações complementares, desconto geral, item de
//     venda com Grupo. Esconde campos de proposta que não aparecem na OS.
export function EditorEstruturado({ dados, setDados, itensVenda, setItensVenda, servicos, revisao, composicaoId, propostaGeradaId, negocioId, tipo = 'proposta', onCriarComposicao, onEditarComposicao, onComposicaoVinculada }: {
  dados: any;
  setDados: React.Dispatch<React.SetStateAction<any>>;
  itensVenda: any[];
  setItensVenda: (i: any[]) => void;
  servicos: any[];
  revisao: number;
  composicaoId: string | null;
  propostaGeradaId?: string | null;
  negocioId?: number | null;
  tipo?: 'proposta' | 'venda';
  onCriarComposicao: () => void;
  onEditarComposicao: (composicaoId: string) => void;
  onComposicaoVinculada?: (composicaoId: string | null, documentoAtualizado?: any) => void;
}) {
  const isVenda = tipo === 'venda';
  const [composicoesDisponiveis, setComposicoesDisponiveis] = useState<any[]>([]);
  const [composicaoVinculada, setComposicaoVinculada] = useState<any>(null);

  // Carrega composições do mesmo negócio (pra oferecer vincular se ainda não houver vínculo)
  useEffect(() => {
    if (!negocioId) return;
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/composicoes?proposta_id=${negocioId}`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        const lista = Array.isArray(data) ? data : [];
        setComposicoesDisponiveis(lista);
        if (composicaoId) {
          setComposicaoVinculada(lista.find((c: any) => c.id === composicaoId) || null);
        } else {
          setComposicaoVinculada(null);
        }
      } catch { /* silencioso */ }
    })();
  }, [negocioId, composicaoId]);

  // Vincula uma composição existente a esta proposta gerada (sem importar conteúdo)
  async function vincularComposicao(novaComposicaoId: string) {
    if (!propostaGeradaId) return;
    try {
      const token = await getToken();
      const res = await axios.put(`${API_URL}/comercial/propostas-geradas/${propostaGeradaId}`,
        { composicao_id: novaComposicaoId },
        { headers: { Authorization: token } });
      const atualizado = res.data.body ? JSON.parse(res.data.body) : res.data;
      if (onComposicaoVinculada) onComposicaoVinculada(novaComposicaoId, atualizado);
    } catch (e: any) {
      alert('Erro ao vincular: ' + (e.response?.data?.error || e.message));
    }
  }

  async function desvincularComposicao() {
    if (!propostaGeradaId) return;
    if (!window.confirm(
      'Desvincular a composição desta proposta?\n\nA composição em si NÃO será excluída — só fica desvinculada. ' +
      'Os itens de venda já preenchidos permanecem (você pode importar de outra composição depois).'
    )) return;
    try {
      const token = await getToken();
      const res = await axios.put(`${API_URL}/comercial/propostas-geradas/${propostaGeradaId}`,
        { composicao_id: null },
        { headers: { Authorization: token } });
      const atualizado = res.data.body ? JSON.parse(res.data.body) : res.data;
      if (onComposicaoVinculada) onComposicaoVinculada(null, atualizado);
    } catch (e: any) {
      alert('Erro ao desvincular: ' + (e.response?.data?.error || e.message));
    }
  }

  async function trocarComposicao() {
    if (!propostaGeradaId || !negocioId) return;
    // Lista TODAS as composições do negócio + outras (busca ampla)
    let todas: any[] = composicoesDisponiveis;
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/composicoes`,
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      todas = Array.isArray(data) ? data : composicoesDisponiveis;
    } catch { /* fallback à lista do negócio */ }

    // Lista as do negócio primeiro, depois outras
    const doNegocio = todas.filter(c => c.proposta_id === negocioId);
    const outras = todas.filter(c => c.proposta_id !== negocioId);
    const opcoes = [...doNegocio, ...outras].filter(c => c.id !== composicaoId);

    if (opcoes.length === 0) {
      alert('Não há outras composições disponíveis. Crie uma nova primeiro.');
      return;
    }
    const linhas = opcoes.map((c: any, i: number) =>
      `${i + 1}) ${c.nome}${c.proposta_id !== negocioId ? ' [outro negócio]' : ''} — ${c.n_itens || 0} itens · ${fmtR(c.total_venda || 0)}`
    ).join('\n');
    const escolha = window.prompt(
      `TROCAR COMPOSIÇÃO\n\nDigite o número da composição que substituirá a atual:\n\n${linhas}\n\n(Dica: para criar uma nova, cancele aqui e clique em "Criar nova".)`,
      '1'
    );
    const idx = parseInt(escolha || '0', 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= opcoes.length) return;
    const novaId = opcoes[idx].id;
    await vincularComposicao(novaId);
  }

  function irParaCriarComposicao() {
    onCriarComposicao();
  }
  function irParaEditarComposicao() {
    if (!composicaoId) return;
    onEditarComposicao(composicaoId);
  }

  // Atalho de teclado: Alt+V adiciona item de venda
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.altKey && (e.key === 'v' || e.key === 'V' || e.code === 'KeyV')) {
        e.preventDefault();
        addItemVenda();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itensVenda]);

  // Busca itens consolidados da composição (1 linha por serviço) e popula itens_venda
  async function importarDaComposicao() {
    if (!composicaoId) {
      alert('Esta proposta não tem composição vinculada. Crie/vincule uma composição primeiro.');
      return;
    }
    if (itensVenda.length > 0) {
      if (!window.confirm(
        `Importar da composição vai SUBSTITUIR os ${itensVenda.length} item(ns) atuais.\n\nContinuar?`
      )) return;
    }
    try {
      const token = await getToken();
      const res = await axios.get(
        `${API_URL}/comercial/composicoes/${composicaoId}/exportar-para-proposta`,
        { headers: { Authorization: token } }
      );
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      const itens = data.itens_venda || [];
      if (itens.length === 0) {
        alert('A composição não tem itens com serviço atribuído. Atribua um serviço a cada item da composição primeiro.');
        return;
      }
      setItensVenda(itens);
      alert(`✓ ${itens.length} serviço(s) importado(s) da composição.\nTotal: R$ ${(data.total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
    } catch (e: any) {
      alert('Erro ao importar: ' + (e.response?.data?.error || e.message));
    }
  }
  function setCampo(k: string, v: any) {
    setDados({ ...dados, [k]: v });
  }
  function setParcela(idx: number, k: string, v: any) {
    const ps = [...(dados.parcelas || [])];
    ps[idx] = { ...ps[idx], [k]: v };
    setDados({ ...dados, parcelas: ps });
  }
  // Edição bidirecional %/valor — guarda STRINGS em estado pra evitar o bug do
  // cursor pular quando React reformata "5" → "5.00" a cada tecla.
  // Quando digitar %: calcula valor e grava as duas strings.
  function setParcelaPct(idx: number, pctStr: string) {
    const ps = [...(dados.parcelas || [])];
    ps[idx] = {
      ...ps[idx],
      ...parcelaPorPercentual(totalVenda, pctStr),
    };
    setDados({ ...dados, parcelas: ps });
  }
  // Quando digitar valor: calcula % e grava as duas strings.
  function setParcelaValor(idx: number, valorStr: string) {
    const ps = [...(dados.parcelas || [])];
    ps[idx] = {
      ...ps[idx],
      ...parcelaPorValor(totalVenda, valorStr),
    };
    setDados({ ...dados, parcelas: ps });
  }
  function addParcela() {
    setDados({ ...dados, parcelas: [...(dados.parcelas || []), {
      descricao: '', percentual: '0', valor_str: '0.00', prazo: '',
      // Sem default pros dias: em branco obriga a decisão consciente. O default
      // silencioso de 0 é justamente o que fazia toda parcela cair no dia da venda.
      dias_apos_assinatura: '', prazo_pagamento_dias: String(PRAZO_PAGAMENTO_DEFAULT),
      fixada: false,
    }] });
  }
  function removerParcela(idx: number) {
    const ps = [...(dados.parcelas || [])];
    ps.splice(idx, 1);
    setDados({ ...dados, parcelas: ps });
  }
  // Rebalanceia parcelas NÃO-fixadas pra completar o TOTAL em R$ (não em %).
  // Mantém o valor das fixas; o restante em reais é dividido igualmente entre
  // as não-fixas, com a ÚLTIMA livre absorvendo o resíduo de centavos — a soma
  // fecha EXATA no total da venda. O % vira derivado (só informativo).
  function rebalancearParcelas() {
    const ps = [...(dados.parcelas || [])];
    if (ps.length === 0) return;
    const valorDe = (p: any) => {
      const v = parseFloat(p.valor_str);
      if (Number.isFinite(v)) return v;
      return (totalVenda * (parseFloat(p.percentual) || 0)) / 100;
    };
    const naoFixas = ps.filter(p => !p.fixada);
    const valorFixas = ps.filter(p => p.fixada).reduce((s, p) => s + valorDe(p), 0);
    const restante = totalVenda - valorFixas;
    if (naoFixas.length === 0) {
      alert('Não há parcelas livres para rebalancear. Desmarque "Fixa" em pelo menos uma.');
      return;
    }
    if (restante < 0) {
      alert(`As parcelas fixas somam ${fmtR(valorFixas)}, acima do total da venda (${fmtR(totalVenda)}). Ajuste antes de rebalancear.`);
      return;
    }
    // Divide em centavos: base truncada pra cada livre; a última absorve o resíduo.
    const base = Math.floor((restante / naoFixas.length) * 100) / 100;
    let idxUltimaLivre = -1;
    ps.forEach((p, i) => { if (!p.fixada) idxUltimaLivre = i; });
    const novas = ps.map((p, i) => {
      if (p.fixada) return p;
      const v = i === idxUltimaLivre
        ? Math.round((restante - base * (naoFixas.length - 1)) * 100) / 100
        : base;
      const pct = totalVenda > 0 ? (v / totalVenda) * 100 : 0;
      return { ...p, valor_str: v.toFixed(2), percentual: pct.toFixed(4) };
    });
    setDados({ ...dados, parcelas: novas });
  }
  function addItemVenda() {
    setItensVenda([...itensVenda, {
      servico_id: null, descricao: '', unidade: 'vb', quantidade: 1, valor_unitario: 0,
    }]);
  }
  function setItem(idx: number, k: string, v: any) {
    const arr = [...itensVenda];
    arr[idx] = { ...arr[idx], [k]: v };
    setItensVenda(arr);
  }
  function selecionarServico(idx: number, servicoId: string) {
    const sp = servicos.find(s => s.id === servicoId);
    if (!sp) {
      setItensVenda(itensVenda.map((it, i) => i === idx ? { ...it, servico_id: null } : it));
      return;
    }
    setItensVenda(itensVenda.map((it, i) => i === idx ? {
      ...it,
      servico_id: sp.id,
      descricao: sp.nome + (sp.descricao ? ` — ${sp.descricao}` : ''),
      unidade: sp.unidade || 'vb',
    } : it));
  }
  function removerItem(idx: number) {
    setItensVenda(itensVenda.filter((_, i) => i !== idx));
  }

  const totalVenda = totalItensVenda(itensVenda);
  // Soma das parcelas por VALOR (o que de fato interessa): usa o R$ digitado
  // (valor_str) quando existe, senão deriva do percentual. "Fecha" quando a
  // diferença pro total é só resíduo de arredondamento (~2 centavos/parcela) —
  // o documento gerado absorve esse resíduo na última parcela e fecha exato.
  const somaParcelasValor = (dados.parcelas || []).reduce((s: number, p: any) => {
    const v = parseFloat(p.valor_str);
    if (Number.isFinite(v)) return s + v;
    return s + (totalVenda * (parseFloat(p.percentual) || 0)) / 100;
  }, 0);
  const difParcelas = totalVenda - somaParcelasValor;
  // Tolerância espelha handlers/parcelasUtil.js: centavos por parcela +
  // ruído do percentual de 4 casas proporcional ao total
  const nParc = (dados.parcelas || []).length || 1;
  const parcelasFecham = Math.abs(difParcelas) <= 0.02 * nParc + 0.01 + totalVenda * 0.000001 * nParc;

  // KICKOFF (2026-08-20) — prazo em dias por parcela. Sem esses números, iniciar
  // o contrato joga TODAS as parcelas na data da assinatura (era o comportamento
  // até aqui). Data de início da execução serve de proxy da assinatura só pra
  // mostrar a previsão; quem manda de verdade é a data escolhida no kick-off.
  const inicioVendaIso = /^\d{4}-\d{2}-\d{2}$/.test(String(dados.data_inicio || ''))
    ? String(dados.data_inicio) : null;
  const parcelasSemPrazoDias = (dados.parcelas || []).filter((p: any) =>
    p?.dias_apos_assinatura === undefined || p?.dias_apos_assinatura === null
    || String(p.dias_apos_assinatura).trim() === ''
  ).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Cabeçalho — info auto-preenchida */}
      <Secao titulo="Cabeçalho (preenchido automaticamente)" cor="#888">
        <div style={{ fontSize: 11, color: '#666', padding: 8, background: '#FAFBFF', borderRadius: 4 }}>
          Cliente, CNPJ, contato, vendedor, data de emissão, número da proposta e <strong>revisão</strong> vêm automaticamente do negócio.
          Você só edita os campos abaixo.
        </div>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600 }}>Revisão atual:</span>
          <span style={{
            fontSize: 13, fontWeight: 700, color: '#020D7F',
            background: '#E7ECFD', padding: '3px 10px', borderRadius: 4,
          }}>R{revisao}</span>
          <span style={{ fontSize: 10, color: '#888' }}>
            Use o botão <strong>Nova revisão</strong> (no rodapé) para criar uma nova versão preservando o histórico.
          </span>
        </div>
      </Secao>

      {/* TEMA / TEMPLATE — escolha do visual da proposta */}
      <Secao titulo="Tema visual" cor="#02B5DB" colapsavel defaultAberta={false}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { id: 'moderno',  nome: 'Moderno (padrão)', desc: 'Tipografia Inter, capa minimalista com gradiente, seções numeradas, tabelas limpas. Estilo SaaS B2B premium.' },
            { id: 'classico', nome: 'Clássico (Ploomes)', desc: 'Capa azul com fundo Ploomes, fonte Verdana, blocos cinza com borda azul. Identidade visual antiga da Hydrodata.' },
          ].map((t) => {
            const ativo = (dados.tema_template || 'moderno') === t.id;
            return (
              <label key={t.id}
                onClick={() => setCampo('tema_template', t.id)}
                style={{
                  cursor: 'pointer', padding: 12, borderRadius: 6,
                  border: `1.5px solid ${ativo ? '#0930D0' : '#e0e0e0'}`,
                  background: ativo ? '#FAFBFF' : '#fff',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="radio" checked={ativo} onChange={() => {}} />
                  <strong style={{ color: ativo ? '#0930D0' : '#333', fontSize: 13 }}>{t.nome}</strong>
                </div>
                <div style={{ fontSize: 10.5, color: '#666', marginTop: 6, lineHeight: 1.5 }}>{t.desc}</div>
              </label>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: '#888', marginTop: 8 }}>
          Trocar o tema só afeta este documento. Salve as alterações para re-renderizar com o novo tema.
        </div>
      </Secao>

      {/* CAPA editável (colapsável; expandida por default — capa fica visível) */}
      <Secao titulo="Capa do documento" cor="#7C3AED" colapsavel defaultAberta>
        <SecaoToggle valor={dados.mostrar_capa !== false}
          onChange={v => setCampo('mostrar_capa', v)}
          label="Mostrar página de capa">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8, marginTop: 8 }}>
            <div>
              <label style={labelMini}>A/C (Aos cuidados de)</label>
              <input value={dados.capa_ac || ''}
                onChange={e => setCampo('capa_ac', e.target.value)}
                placeholder="Padrão: nome do contato"
                style={inputBase} />
            </div>
            <div>
              <label style={labelMini}>Slogan / subtítulo (opcional)</label>
              <input value={dados.capa_slogan || ''}
                onChange={e => setCampo('capa_slogan', e.target.value)}
                placeholder="Ex.: Inteligência hídrica completa"
                style={inputBase} />
            </div>
          </div>
          <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>
            Cliente, título, revisão e data são preenchidos automaticamente.
          </div>
        </SecaoToggle>
      </Secao>

      {/* INSTITUCIONAL editável (Quem somos + Serviços) — só pra propostas */}
      {!isVenda && (
        <Secao titulo="Página institucional (Quem Somos / Serviços)" cor="#7C3AED" colapsavel defaultAberta={false}>
          <SecaoToggle valor={dados.mostrar_institucional === true}
            onChange={v => setCampo('mostrar_institucional', v)}
            label="Mostrar página institucional">
            <RichTextEditor
              value={dados.institucional_html || ''}
              onChange={(v) => setCampo('institucional_html', v)}
              height={250} />
          </SecaoToggle>
        </Secao>
      )}

      {/* ═══ Bloco específico VENDA — Empresa Contratada (Hydrodata) ═══ */}
      {isVenda && (
        <Secao titulo="Empresa Contratada (Hydrodata)" cor="#020D7F" colapsavel defaultAberta>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 10 }}>
            Dados da Hydrodata que aparecem na seção <strong>Contratada</strong> do documento.
            Pode ser sobrescrito por documento se necessário.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelMini}>Razão Social</label>
              <input value={dados.empresa_contratada?.razao_social || 'Hydrodata Engenharia Ltda.'}
                onChange={e => setCampo('empresa_contratada', { ...(dados.empresa_contratada || {}), razao_social: e.target.value })}
                style={inputBase} />
            </div>
            <div>
              <label style={labelMini}>CNPJ</label>
              <input value={dados.empresa_contratada?.cnpj || ''}
                onChange={e => setCampo('empresa_contratada', { ...(dados.empresa_contratada || {}), cnpj: e.target.value })}
                placeholder="00.000.000/0000-00"
                style={inputBase} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelMini}>Endereço</label>
              <input value={dados.empresa_contratada?.endereco || ''}
                onChange={e => setCampo('empresa_contratada', { ...(dados.empresa_contratada || {}), endereco: e.target.value })}
                placeholder="Rua, número"
                style={inputBase} />
            </div>
            <div>
              <label style={labelMini}>Bairro</label>
              <input value={dados.empresa_contratada?.bairro || ''}
                onChange={e => setCampo('empresa_contratada', { ...(dados.empresa_contratada || {}), bairro: e.target.value })}
                style={inputBase} />
            </div>
            <div>
              <label style={labelMini}>Cidade / UF</label>
              <input value={dados.empresa_contratada?.cidade || ''}
                onChange={e => setCampo('empresa_contratada', { ...(dados.empresa_contratada || {}), cidade: e.target.value })}
                placeholder="Ex.: Porto Alegre / RS"
                style={inputBase} />
            </div>
            <div>
              <label style={labelMini}>CEP</label>
              <input value={dados.empresa_contratada?.cep || ''}
                onChange={e => setCampo('empresa_contratada', { ...(dados.empresa_contratada || {}), cep: e.target.value })}
                placeholder="00000-000"
                style={inputBase} />
            </div>
          </div>
        </Secao>
      )}

      {/* Prazo / previsão — só pra propostas (venda usa o bloco "Prazo de execução" abaixo) */}
      {!isVenda && (
        <Secao titulo="Prazo / Previsão de entrega">
          <input type="text" value={dados.prazo_entrega || ''}
            onChange={e => setCampo('prazo_entrega', e.target.value)}
            placeholder="Ex.: 90 dias úteis após assinatura"
            style={inputBase} />
        </Secao>
      )}

      {/* Validade — editável (aparece no cabeçalho e nas condições da proposta) */}
      {!isVenda && (
        <Secao titulo="Validade da proposta">
          <input type="text" value={dados.validade || ''}
            onChange={e => setCampo('validade', e.target.value)}
            placeholder="Ex.: 30 dias · ou 8 de agosto de 2026"
            style={inputBase} />
          <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
            Deixe em branco para usar o padrão (30 dias a partir da geração).
          </div>
        </Secao>
      )}

      {/* ═══ Bloco específico VENDA — Objeto (1 linha texto + ref proposta origem) ═══ */}
      {isVenda && (
        <Secao titulo="Objeto da Ordem de Serviço">
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            O texto do objeto é gerado automaticamente: <em>"A presente Ordem de Serviço tem por objeto o serviço de
            <strong> [título do projeto]</strong>, conforme proposta nº [origem]..."</em>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelMini}>Proposta de origem (número)</label>
              <input value={dados.proposta_origem_numero || ''}
                onChange={e => setCampo('proposta_origem_numero', e.target.value)}
                placeholder="Ex.: PRP-2026-0017"
                style={inputBase} />
            </div>
            <div>
              <label style={labelMini}>Revisão da proposta de origem</label>
              <input type="number" min="0" value={dados.proposta_origem_revisao ?? 0}
                onChange={e => setCampo('proposta_origem_revisao', parseInt(e.target.value, 10) || 0)}
                style={inputBase} />
            </div>
          </div>
        </Secao>
      )}

      {/* ═══ Escopo técnico — na VENDA é o Anexo I (17/09/2026) ═══════════
           🚨 Os três campos de escopo (técnico, entregáveis, cronograma)
           ficavam ESCONDIDOS quando o documento era venda: o texto só chegava
           copiado da proposta, na criação, e depois ninguém podia tocá-lo.
           Com o Anexo I o escopo passou a ser parte do documento que o cliente
           assina, e o envio para assinatura RECUSA venda sem ele — mandando
           preencher aqui. Sem estes campos a recusa pediria o impossível
           (o único botão "Importar dados" da tela traz a composição de PREÇOS,
           não o escopo), e as vendas montadas do zero ou importadas do Ploomes
           nunca poderiam ser formalizadas. */}
      <Secao titulo={isVenda ? 'Escopo técnico — vai no Anexo I' : 'Objeto / Descrição técnica'}>
        {isVenda && (
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            Sai impresso como <strong>Anexo I — Proposta Técnica</strong>, depois das Disposições
            Gerais, no mesmo PDF que o cliente assina. Não escreva valores aqui: o preço é o do
            quadro "Produtos e Serviços".
          </div>
        )}
        <RichTextEditor value={dados.descricao_tecnica || ''}
          onChange={(v) => setCampo('descricao_tecnica', v)} height={200} />
      </Secao>

      {/* Painel de composição vinculada (sempre visível, mostra o estado e oferece ações) */}
      <Secao titulo="🧮 Composição de preços" cor="#7C3AED" colapsavel defaultAberta>
        {composicaoId && composicaoVinculada ? (
          <div style={{ padding: 10, background: '#0a7f4a15', border: '0.5px solid #0a7f4a', borderRadius: 5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 12 }}>
                ✓ Vinculada: <strong>{composicaoVinculada.nome}</strong>
                <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>
                  {composicaoVinculada.n_itens} item(ns) · venda total {fmtR(composicaoVinculada.total_venda || 0)} · status {composicaoVinculada.status}
                </div>
              </div>
              <button type="button" onClick={importarDaComposicao}
                title="Lê a composição e SUBSTITUI os itens de venda (1 linha por serviço)"
                style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  border: '0.5px solid #0a7f4a', background: '#0a7f4a', color: '#fff',
                  borderRadius: 5, cursor: 'pointer' }}>↓ Importar dados</button>
              <button type="button" onClick={irParaEditarComposicao}
                style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  border: '0.5px solid #0a7f4a', background: '#fff', color: '#0a7f4a',
                  borderRadius: 5, cursor: 'pointer' }}>↗ Editar</button>
              <button type="button" onClick={trocarComposicao}
                title="Substituir esta composição vinculada por outra"
                style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  border: '0.5px solid #B07500', background: '#fff', color: '#B07500',
                  borderRadius: 5, cursor: 'pointer' }}>⇄ Trocar</button>
              <button type="button" onClick={desvincularComposicao}
                title="Desvincular composição (não exclui a composição em si)"
                style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  border: '0.5px solid #B71C1C', background: '#fff', color: '#B71C1C',
                  borderRadius: 5, cursor: 'pointer' }}>✕ Desvincular</button>
            </div>
          </div>
        ) : composicoesDisponiveis.length > 0 ? (
          <div style={{ padding: 10, background: '#FFF8E8', border: '0.5px solid #B07500', borderRadius: 5 }}>
            <div style={{ fontSize: 12, color: '#444', marginBottom: 6 }}>
              Esta proposta não tem composição vinculada, mas o negócio já tem {composicoesDisponiveis.length} composição(ões):
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <select onChange={e => e.target.value && vincularComposicao(e.target.value)} defaultValue=""
                style={{ flex: 1, minWidth: 200, padding: '6px 8px', fontSize: 12, borderRadius: 4, border: '0.5px solid #B07500' }}>
                <option value="" disabled>Selecione pra vincular...</option>
                {composicoesDisponiveis.map((c: any) => (
                  <option key={c.id} value={c.id}>{c.nome} ({c.n_itens} itens · {fmtR(c.total_venda || 0)})</option>
                ))}
              </select>
              <button type="button" onClick={irParaCriarComposicao}
                style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  border: '0.5px solid #B07500', background: '#fff', color: '#B07500',
                  borderRadius: 5, cursor: 'pointer' }}>+ Criar nova</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 10, background: '#F0F1F5', border: '0.5px dashed #aaa', borderRadius: 5 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              Esta proposta não tem composição vinculada — itens de venda estão sendo preenchidos manualmente.
              Você pode criar uma composição agora e depois importar os serviços direto pra cá.
            </div>
            <button type="button" onClick={irParaCriarComposicao}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600,
                border: 'none', borderRadius: 5, background: '#7C3AED', color: '#fff', cursor: 'pointer' }}>
              + Criar composição vinculada a esta proposta
            </button>
          </div>
        )}
      </Secao>

      {/* Itens de venda */}
      <Secao titulo={`${isVenda ? 'Produtos e Serviços' : 'Itens de venda'} — total ${fmtR(totalVenda)}`} cor="#0930D0">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: '#888', flex: 1 }}>
            {isVenda
              ? <>Itens que aparecem na tabela <strong>Produtos e Serviços</strong> da Ordem de Serviço. Coluna <strong>Grupo</strong> é específica da OS (ex.: "Topografia", "Hidrologia").</>
              : <>Linhas que aparecem na proposta para o cliente. <strong>Não confunda com a composição</strong> (que é interna): cada linha aqui é um <strong>serviço</strong> de venda, com a descrição que o cliente verá.</>}
          </div>
        </div>
        {isVenda && (
          <div style={{ display: 'grid', gridTemplateColumns: '180px 180px', gap: 12, marginBottom: 12, padding: 10, background: '#FAFBFF', borderRadius: 5 }}>
            <div>
              <label style={labelMini}>Desconto geral (%)</label>
              <input type="number" step="0.1" min="0" max="100" value={dados.desconto_geral_pct || 0}
                onChange={e => setCampo('desconto_geral_pct', parseFloat(e.target.value) || 0)}
                style={inputBase} />
            </div>
            <div style={{ alignSelf: 'flex-end', fontSize: 11, color: '#0a7f4a', fontWeight: 600 }}>
              {(parseFloat(dados.desconto_geral_pct) || 0) > 0
                ? `Total c/ desconto: ${fmtR(totalVenda * (1 - (parseFloat(dados.desconto_geral_pct) || 0) / 100))}`
                : 'Sem desconto geral aplicado'}
            </div>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#020D7F', color: '#fff' }}>
              <th style={thMini}>#</th>
              {isVenda && <th style={{ ...thMini, width: 110 }}>Grupo</th>}
              <th style={thMini}>{isVenda ? 'Produto/Serviço' : 'Serviço / Descrição'}</th>
              <th style={{ ...thMini, width: 70 }}>Unid.</th>
              <th style={{ ...thMini, width: 70, textAlign: 'right' }}>Qtd.</th>
              <th style={{ ...thMini, width: 110, textAlign: 'right' }}>Valor unit.</th>
              {isVenda && <th style={{ ...thMini, width: 70, textAlign: 'right' }}>Desc. %</th>}
              <th style={{ ...thMini, width: 110, textAlign: 'right' }}>Total</th>
              <th style={{ ...thMini, width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {itensVenda.map((it, i) => {
              const q = parseFloat(it.quantidade) || 0;
              const v = parseFloat(it.valor_unitario) || 0;
              const desc = parseFloat(it.desconto_pct) || 0;
              // Em venda, total considera desconto da linha; em proposta, total = q*v puro
              const t = isVenda ? q * v * (1 - desc / 100) : q * v;
              const servicoVinculado = it.servico_id ? servicos.find((s: any) => s.id === it.servico_id) : null;
              return (
                <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                  <td style={tdMini}>{i + 1}</td>
                  {isVenda && (
                    <td style={tdEditMini}>
                      <input value={it.grupo || ''}
                        onChange={e => setItem(i, 'grupo', e.target.value)}
                        placeholder="Ex.: Topografia"
                        style={inputSm} />
                    </td>
                  )}
                  <td style={tdEditMini}>
                    <ServicoComboBox
                      item={it}
                      servicos={servicos}
                      onSelecionarServico={(s) => selecionarServico(i, s.id)}
                      onChangeDescricao={(v) => setItem(i, 'descricao', v)}
                      onUnlink={() => setItem(i, 'servico_id', null)}
                      servicoVinculado={servicoVinculado || null}
                    />
                  </td>
                  <td style={tdEditMini}>
                    <input value={it.unidade || ''} onChange={e => setItem(i, 'unidade', e.target.value)}
                      style={inputSm} />
                  </td>
                  <td style={tdEditMini}>
                    <input type="number" step="0.01" value={it.quantidade || 0}
                      onChange={e => setItem(i, 'quantidade', parseFloat(e.target.value) || 0)}
                      style={{ ...inputSm, textAlign: 'right' }} />
                  </td>
                  <td style={{ ...tdEditMini, minWidth: 140, whiteSpace: 'nowrap' }}>
                    <InputNumber {...PROPS_DINHEIRO_BR} precision={2} min={0}
                      value={it.valor_unitario || 0}
                      onChange={v => setItem(i, 'valor_unitario', Number(v) || 0)}
                      style={{ width: '100%', minWidth: 130 }} />
                  </td>
                  {isVenda && (
                    <td style={tdEditMini}>
                      <input type="number" step="0.1" min="0" max="100" value={it.desconto_pct || 0}
                        onChange={e => setItem(i, 'desconto_pct', parseFloat(e.target.value) || 0)}
                        style={{ ...inputSm, textAlign: 'right' }} />
                    </td>
                  )}
                  <td style={{ ...tdMini, minWidth: 140, whiteSpace: 'nowrap', textAlign: 'right',
                    fontWeight: 500, color: '#020D7F', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtR(t)}
                  </td>
                  <td style={{ ...tdMini, textAlign: 'center' }}>
                    <button type="button" onClick={() => removerItem(i)}
                      style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, border: '0.5px solid #ddd',
                        background: '#fff', cursor: 'pointer', color: '#B91626' }}>×</button>
                  </td>
                </tr>
              );
            })}
            {itensVenda.length === 0 && (
              <tr><td colSpan={isVenda ? 9 : 7} style={{ padding: 14, textAlign: 'center', color: '#aaa', fontSize: 11 }}>
                Nenhum item ainda. Use <strong>+ Adicionar item</strong> ou <strong>↓ Importar da composição</strong>.
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
        <button type="button" onClick={addItemVenda}
          title="Adicionar item (Alt+V)"
          style={{ marginTop: 8, padding: '5px 12px', fontSize: 11, borderRadius: 4,
            border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0', cursor: 'pointer', fontWeight: 500 }}>
          + Adicionar item <span style={{ fontSize: 9, opacity: 0.7, marginLeft: 4 }}>Alt+V</span>
        </button>
      </Secao>

      {/* Entregáveis e Cronograma — na venda, as outras duas seções do Anexo I */}
      <Secao titulo={isVenda ? 'Entregáveis — vão no Anexo I' : 'Entregáveis'}>
        <RichTextEditor value={dados.entregaveis || ''}
          onChange={(v) => setCampo('entregaveis', v)} height={150} />
      </Secao>
      <Secao titulo={isVenda ? 'Cronograma — vai no Anexo I' : 'Cronograma'}>
        {isVenda && (
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            Confira se o cronograma não contradiz o "Prazo de Execução" abaixo: os dois vão no
            mesmo documento assinado.
          </div>
        )}
        <RichTextEditor value={dados.cronograma || ''}
          onChange={(v) => setCampo('cronograma', v)} height={150} />
      </Secao>

      {/* ═══ Bloco específico VENDA — Prazo de Execução (3 cards) ═══
           [#d161bf7d] Datas dinâmicas: prazo_dias auto-calcula data_conclusao_prevista */}
      {isVenda && (
        <Secao titulo="Prazo de Execução" cor="#020D7F">
          <RangePrazoVenda dados={dados} setDados={setDados} />
        </Secao>
      )}

      {/* Parcelas */}
      <Secao titulo={parcelasFecham
          ? `Parcelas — ✓ fecham o total (${fmtR(totalVenda)})`
          : `Parcelas — somam ${fmtR(somaParcelasValor)} de ${fmtR(totalVenda)} (${difParcelas > 0 ? 'faltam' : 'sobram'} ${fmtR(Math.abs(difParcelas))})`}
        cor={parcelasFecham ? '#0a7f4a' : '#B07500'}>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#020D7F', color: '#fff' }}>
              <th style={thMini}>Descrição</th>
              <th style={{ ...thMini, width: 80, textAlign: 'right' }}>%</th>
              <th style={{ ...thMini, width: 140, textAlign: 'right' }}>Valor (R$)</th>
              <th style={thMini} title="Texto que o cliente lê no documento. Não define data nenhuma — quem define são as duas colunas seguintes.">Prazo (texto)</th>
              <th style={{ ...thMini, width: 90, textAlign: 'right' }}
                title="Quantos dias depois da assinatura do contrato esta parcela é faturada (emissão da NF). 0 = na assinatura.">
                Emitir<br />+ dias</th>
              <th style={{ ...thMini, width: 90, textAlign: 'right' }}
                title="Quantos dias o cliente tem pra pagar depois da emissão da NF.">
                Pagto<br />+ dias</th>
              <th style={{ ...thMini, width: 96, textAlign: 'right' }}
                title="Previsão calculada. Só aparece com data de início preenchida no bloco de Prazo.">
                Vencimento</th>
              <th style={{ ...thMini, width: 50, textAlign: 'center' }} title="Marca a parcela como fixa: o botão Atualizar redistribui o restante entre as não-fixas">Fixa</th>
              <th style={{ ...thMini, width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {(dados.parcelas || []).map((p: any, i: number) => {
              // Usa as strings vivas (percentual / valor_str) pra não reformatar
              // durante a edição. Fallback pra valor antigo (número) das propostas
              // pré-existentes que não tinham valor_str.
              const pctStr = p.percentual !== undefined && p.percentual !== null
                ? String(p.percentual) : '0';
              const pctNum = parseFloat(pctStr) || 0;
              const valorStr = p.valor_str !== undefined && p.valor_str !== null
                ? p.valor_str
                : ((totalVenda * pctNum) / 100).toFixed(2);
              // Prazos em dias — o que realmente vira data no plano financeiro.
              // Em branco ≠ zero: em branco é pendência, zero é "na assinatura".
              const diasEmissaoStr = p.dias_apos_assinatura === undefined || p.dias_apos_assinatura === null
                ? '' : String(p.dias_apos_assinatura);
              const semDiasEmissao = diasEmissaoStr.trim() === '';
              const diasPagtoStr = p.prazo_pagamento_dias === undefined || p.prazo_pagamento_dias === null
                ? '' : String(p.prazo_pagamento_dias);
              const diasPagtoNum = diasPagtoStr.trim() === ''
                ? PRAZO_PAGAMENTO_DEFAULT : (parseInt(diasPagtoStr, 10) || 0);
              // Previsão só quando há data de início da execução preenchida no
              // bloco de Prazo — é a melhor proxy da assinatura nesta tela.
              const emissaoPrev = semDiasEmissao ? null
                : somarDias(inicioVendaIso, parseInt(diasEmissaoStr, 10) || 0);
              const vencimentoPrev = somarDias(emissaoPrev, diasPagtoNum);
              return (
                <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0',
                  background: p.fixada ? '#FFF8E8' : 'transparent' }}>
                  <td style={tdEditMini}>
                    <input value={p.descricao || ''} onChange={e => setParcela(i, 'descricao', e.target.value)}
                      style={inputSm} />
                  </td>
                  <td style={tdEditMini}>
                    <input type="number" step="0.01" value={pctStr}
                      onChange={e => setParcelaPct(i, e.target.value)}
                      style={{ ...inputSm, textAlign: 'right' }} />
                  </td>
                  <td style={{ ...tdEditMini, minWidth: 140, whiteSpace: 'nowrap' }}>
                    <InputNumber {...PROPS_DINHEIRO_BR} precision={2} min={0}
                      value={valorStr || null}
                      onChange={v => setParcelaValor(i, v == null ? '' : String(v))}
                      title={totalVenda > 0 ? 'Edite o valor — o % é recalculado automaticamente' : 'Adicione itens à venda primeiro (total = 0)'}
                      disabled={totalVenda <= 0}
                      style={{ width: '100%', minWidth: 130, color: '#020D7F', fontWeight: 500 }} />
                  </td>
                  <td style={tdEditMini}>
                    <input value={p.prazo || ''} onChange={e => setParcela(i, 'prazo', e.target.value)}
                      placeholder="Ex.: Na assinatura" style={inputSm} />
                  </td>
                  <td style={tdEditMini}>
                    <input type="number" step="1" min="0" value={diasEmissaoStr}
                      onChange={e => setParcela(i, 'dias_apos_assinatura', e.target.value)}
                      placeholder="—"
                      style={{ ...inputSm, textAlign: 'right',
                        borderColor: semDiasEmissao ? '#B91626' : undefined,
                        background: semDiasEmissao ? '#FDECEC' : undefined }} />
                  </td>
                  <td style={tdEditMini}>
                    <input type="number" step="1" min="0" value={diasPagtoStr}
                      onChange={e => setParcela(i, 'prazo_pagamento_dias', e.target.value)}
                      placeholder={String(PRAZO_PAGAMENTO_DEFAULT)}
                      style={{ ...inputSm, textAlign: 'right' }} />
                  </td>
                  <td style={{ ...tdMini, textAlign: 'right', fontSize: 11,
                    color: vencimentoPrev ? '#020D7F' : '#bbb' }}>
                    {semDiasEmissao ? '—' : (vencimentoPrev
                      ? fmtDataBr(vencimentoPrev)
                      : `assin. +${(parseInt(diasEmissaoStr, 10) || 0) + diasPagtoNum}d`)}
                  </td>
                  <td style={{ ...tdEditMini, textAlign: 'center' }}>
                    <input type="checkbox" checked={!!p.fixada}
                      onChange={e => setParcela(i, 'fixada', e.target.checked)}
                      title="Quando fixada, 'Atualizar' não muda essa parcela — só rebalanceia as demais" />
                  </td>
                  <td style={{ ...tdMini, textAlign: 'center' }}>
                    <button type="button" onClick={() => removerParcela(i)}
                      style={{ fontSize: 11, padding: '2px 6px', borderRadius: 3, border: '0.5px solid #ddd',
                        background: '#fff', cursor: 'pointer', color: '#B91626' }}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <button type="button" onClick={addParcela}
            style={{ padding: '5px 12px', fontSize: 11, borderRadius: 4,
              border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0', cursor: 'pointer', fontWeight: 500 }}>
            + Adicionar parcela
          </button>
          <button type="button" onClick={rebalancearParcelas}
            disabled={(dados.parcelas || []).length === 0}
            title="Mantém o valor das parcelas marcadas como Fixa e divide o restante em R$ igualmente entre as não-fixas — a soma fecha exata no total da venda"
            style={{ padding: '5px 12px', fontSize: 11, borderRadius: 4,
              border: '0.5px solid #0a7f4a', background: '#fff', color: '#0a7f4a',
              cursor: (dados.parcelas || []).length === 0 ? 'not-allowed' : 'pointer', fontWeight: 500,
              opacity: (dados.parcelas || []).length === 0 ? 0.5 : 1 }}>
            🔄 Atualizar (rebalancear)
          </button>
          {totalVenda <= 0 && (
            <span style={{ fontSize: 10, color: '#B07500' }}>
              Total venda = 0 — edição por valor desabilitada. Adicione itens primeiro.
            </span>
          )}
        </div>
        {parcelasSemPrazoDias > 0 ? (
          <div style={{ marginTop: 8, fontSize: 11, color: '#B91626',
            background: '#FDECEC', border: '0.5px solid #F5C2C2', borderRadius: 4, padding: '6px 8px' }}>
            ⚠ {parcelasSemPrazoDias} parcela(s) sem <strong>“Emitir + dias”</strong>. Sem esse número
            a parcela nasce faturando no dia da assinatura — preencha antes de gerar a venda.
          </div>
        ) : (dados.parcelas || []).length > 0 && (
          <div style={{ marginTop: 8, fontSize: 10.5, color: '#666' }}>
            Emissão da NF = assinatura + “Emitir + dias”. Vencimento = emissão + “Pagto + dias”.
            {inicioVendaIso
              ? <> A coluna Vencimento usa <strong>{fmtDataBr(inicioVendaIso)}</strong> (início da execução) como assinatura provável.</>
              : <> Preencha a data de início no bloco de Prazo pra ver as datas previstas.</>}
          </div>
        )}
      </Secao>

      {/* Condições gerais (3 sub-blocos) + Pagamento (Medições/Condições texto) — só pra propostas */}
      {!isVenda && (
        <>
          <Secao titulo="Condições gerais — Itens que NÃO integram a proposta">
            <RichTextEditor value={dados.itens_nao_integram || ''}
              onChange={(v) => setCampo('itens_nao_integram', v)} height={120} />
          </Secao>

          <Secao titulo="Condições gerais — Responsabilidades da Contratada">
            <RichTextEditor value={dados.responsabilidades_contratada || ''}
              onChange={(v) => setCampo('responsabilidades_contratada', v)} height={120} />
          </Secao>

          <Secao titulo="Condições gerais — Responsabilidades da Contratante">
            <RichTextEditor value={dados.responsabilidades_contratante || ''}
              onChange={(v) => setCampo('responsabilidades_contratante', v)} height={120} />
          </Secao>

          <Secao titulo="Pagamento — Medições">
            <RichTextEditor value={dados.medicoes || ''}
              onChange={(v) => setCampo('medicoes', v)} height={120} />
          </Secao>

          <Secao titulo="Pagamento — Condições">
            <RichTextEditor value={dados.condicoes_pagamento || ''}
              onChange={(v) => setCampo('condicoes_pagamento', v)} height={140} />
          </Secao>
        </>
      )}

      {/* ═══ Bloco específico VENDA — Forma de Pagamento (texto livre) ═══ */}
      {isVenda && (
        <Secao titulo="Forma de Pagamento" cor="#020D7F">
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            Texto descrevendo a forma de pagamento (boleto, transferência, parcelas, retenções, etc).
            Aparece logo abaixo da tabela de parcelas no documento.
          </div>
          <RichTextEditor
            value={dados.forma_pagamento || ''}
            onChange={(v) => setCampo('forma_pagamento', v)}
            height={140} />
        </Secao>
      )}

      {/* ═══ Bloco específico VENDA — Informações Complementares ═══ */}
      {isVenda && (
        <Secao titulo="Informações Complementares" cor="#7C3AED" colapsavel defaultAberta={false}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            Cláusulas adicionais, observações, condições especiais. Opcional — só aparece no documento se preenchido.
          </div>
          <RichTextEditor
            value={dados.informacoes_complementares || ''}
            onChange={(v) => setCampo('informacoes_complementares', v)}
            height={180} />
        </Secao>
      )}

      {/* Dados Bancários editável — só pra propostas (na OS o cliente já recebeu da proposta) */}
      {!isVenda && (
        <Secao titulo="Dados bancários" cor="#7C3AED">
          <SecaoToggle valor={dados.mostrar_dados_bancarios !== false}
            onChange={v => setCampo('mostrar_dados_bancarios', v)}
            label="Mostrar dados bancários no documento">
            <RichTextEditor
              value={dados.dados_bancarios_html || ''}
              onChange={(v) => setCampo('dados_bancarios_html', v)}
              height={140} />
          </SecaoToggle>
        </Secao>
      )}

      {/* Assinatura — toggle + editor inline dos dados do vendedor */}
      <Secao titulo="Assinatura / Vendedor" cor="#7C3AED" colapsavel defaultAberta>
        <SecaoToggle valor={dados.mostrar_assinatura !== false}
          onChange={v => setCampo('mostrar_assinatura', v)}
          label="Mostrar bloco de assinatura no fim do documento">
          <PreviewAssinaturaNegocio
            override={dados.vendedor_dados}
            onChangeOverride={(v) => setCampo('vendedor_dados', v)}
            saudacao={dados.assinatura_saudacao ?? 'Fico à disposição,'}
            onChangeSaudacao={(s) => setCampo('assinatura_saudacao', s)}
            negocioId={negocioId}
          />
        </SecaoToggle>
      </Secao>
    </div>
  );
}

// ─── PreviewAssinaturaNegocio ───
// Mostra o vendedor que vem AUTOMATICAMENTE do negócio (responsavel/vendedor_email
// + perfil cadastrado em Admin). NÃO há dropdown — para trocar o vendedor de um
// negócio, edite o negócio em si. Aqui apenas:
//  - Mostra o preview da assinatura (com dados do negócio + cadastro)
//  - Permite override manual pontual (caso queira algo diferente nesta proposta)
function PreviewAssinaturaNegocio({ override, onChangeOverride, saudacao, onChangeSaudacao, negocioId }: {
  override: any;
  onChangeOverride: (v: any) => void;
  saudacao: string;
  onChangeSaudacao: (s: string) => void;
  negocioId?: number | null;
}) {
  const [vendedorNeg, setVendedorNeg] = useState<any>(null);
  const [carregando, setCarregando] = useState(true);
  const [edicaoManual, setEdicaoManual] = useState(false);

  // Carrega o vendedor preview do negócio (chama um GET que retorna ctx.vendedor)
  useEffect(() => {
    if (!negocioId) { setCarregando(false); return; }
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/comercial/propostas?vendedor_preview=1&id=${negocioId}`,
          { headers: { Authorization: token } });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        setVendedorNeg(data || {});
      } catch { setVendedorNeg({}); }
      finally { setCarregando(false); }
    })();
  }, [negocioId]);

  // Vendedor efetivo: contexto do negócio + override (override só sobrescreve se não-vazio)
  const v: any = { ...(vendedorNeg || {}) };
  if (override) {
    for (const [k, val] of Object.entries(override)) {
      if (val !== undefined && val !== null && val !== '') v[k] = val;
    }
  }

  const temOverride = override && Object.values(override).some(x => x !== undefined && x !== null && x !== '');

  return (
    <div>
      {/* Saudação */}
      <div style={{ marginBottom: 10 }}>
        <label style={lbl}>Saudação de fechamento</label>
        <input value={saudacao}
          onChange={e => onChangeSaudacao(e.target.value)}
          placeholder="Ex.: Fico à disposição," style={inputBase} />
      </div>

      {/* Aviso: vendedor vem do negócio */}
      <div style={{
        padding: '8px 10px', marginBottom: 10, borderRadius: 5,
        background: '#F0F4FF', border: '0.5px solid #c8d2f7', fontSize: 11, color: '#020D7F',
      }}>
        ℹ O vendedor é definido pelo <strong>responsável do negócio</strong> e seu cadastro em
        <em> Admin → Usuários</em> (cargo, foto, telefone, CREA, LinkedIn).
        Para mudar o vendedor desta proposta, edite o negócio.
      </div>

      {/* Preview da assinatura */}
      <div style={{
        padding: 12, border: '0.5px solid #ddd', borderRadius: 6,
        background: '#FAFBFF', display: 'flex', gap: 14, alignItems: 'center',
      }}>
        {v.foto_url ? (
          <img src={v.foto_url} alt=""
            style={{ width: 70, height: 70, borderRadius: '50%', objectFit: 'cover', border: '0.5px solid #ddd' }}
            onError={(e: any) => { e.target.style.display = 'none'; }} />
        ) : (
          <div style={{ width: 70, height: 70, borderRadius: '50%', background: '#e8e8ea',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#aaa' }}>
            ?
          </div>
        )}
        <div style={{ flex: 1, fontSize: 11, lineHeight: 1.5 }}>
          {carregando ? (
            <span style={{ color: '#888' }}>Carregando vendedor do negócio...</span>
          ) : (
            <>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{v.nome || <em style={{ color: '#aa3939' }}>Negócio sem responsável atribuído</em>}</div>
              {v.cargo && <div style={{ color: '#555', fontStyle: 'italic' }}>{v.cargo}</div>}
              {v.email && <div>E-mail: {v.email}</div>}
              {v.telefone && <div>Tel.: {v.telefone}</div>}
              {v.crea && <div>CREA: {v.crea}</div>}
              {v.linkedin && <div>LinkedIn: {v.linkedin}</div>}
            </>
          )}
        </div>
        <button type="button" onClick={() => setEdicaoManual(x => !x)}
          style={{ padding: '5px 10px', fontSize: 10, fontWeight: 600,
            border: '0.5px solid #7C3AED', background: '#fff', color: '#7C3AED',
            borderRadius: 4, cursor: 'pointer' }}>
          {edicaoManual ? '↩ Fechar' : '✎ Override manual'}
        </button>
      </div>

      {temOverride && !edicaoManual && (
        <div style={{ marginTop: 6, fontSize: 10, color: '#B07500' }}>
          ⚠ Esta proposta tem override manual ativo (alguns campos diferentes do cadastro).
          <button type="button"
            onClick={() => onChangeOverride({})}
            style={{ marginLeft: 6, fontSize: 10, color: '#aa3939', background: 'transparent',
              border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
            limpar override
          </button>
        </div>
      )}

      {/* Edição manual (override) */}
      {edicaoManual && (
        <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '0.5px dashed #aaa', borderRadius: 6 }}>
          <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
            Override pontual nesta proposta. Para mudar definitivamente o vendedor, edite o cadastro em <strong>Admin → Usuários</strong>.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              ['Nome', 'nome'],
              ['Cargo', 'cargo'],
              ['E-mail', 'email'],
              ['Telefone', 'telefone'],
              ['CREA', 'crea'],
              ['LinkedIn', 'linkedin'],
            ].map(([label, key]) => (
              <div key={key}>
                <label style={lbl}>{label}</label>
                <input value={(override && override[key]) || ''}
                  onChange={e => onChangeOverride({ ...(override || {}), [key]: e.target.value })}
                  placeholder={`Padrão: ${vendedorNeg?.[key] || '—'}`}
                  style={inputBase} />
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>URL da foto (sobrescreve a foto do cadastro)</label>
              <input value={(override && override.foto_url) || ''}
                onChange={e => onChangeOverride({ ...(override || {}), foto_url: e.target.value })}
                placeholder={vendedorNeg?.foto_url || 'https://...'}
                style={inputBase} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Combobox de serviço — UX similar à de descrição da composição (clique no ícone abre dropdown com busca).
// Permite escolher do catálogo OU descrever livremente. Sem linhas duplicadas para descrição.
function ServicoComboBox({ item, servicos, onSelecionarServico, onChangeDescricao, onUnlink, servicoVinculado }: {
  item: any;
  servicos: any[];
  onSelecionarServico: (s: any) => void;
  onChangeDescricao: (v: string) => void;
  onUnlink: () => void;
  servicoVinculado: any | null;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const filtrados = busca
    ? servicos.filter(s => {
        const q = busca.toLowerCase();
        return (s.nome || '').toLowerCase().includes(q)
            || (s.codigo || '').toLowerCase().includes(q)
            || (s.area || '').toLowerCase().includes(q);
      })
    : servicos;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 3 }}>
        <input value={item.descricao || ''}
          onChange={e => onChangeDescricao(e.target.value)}
          placeholder="Descrição livre ou clique 📚 para escolher do catálogo"
          style={{ ...inputSm, flex: 1, fontSize: 11 }} />
        <button type="button" onClick={() => { setAberto(a => !a); setBusca(''); }}
          title="Selecionar serviço do catálogo"
          style={{
            padding: '4px 8px', fontSize: 12, borderRadius: 3,
            border: `0.5px solid ${servicoVinculado ? '#0930D0' : '#ccc'}`,
            background: servicoVinculado ? '#E7ECFD' : '#fff',
            cursor: 'pointer',
          }}>📚</button>
      </div>
      {servicoVinculado && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <span style={{
            fontSize: 10, background: '#E7ECFD', color: '#0930D0',
            padding: '1px 6px', borderRadius: 3, fontWeight: 500,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            🔗 {servicoVinculado.codigo ? `[${servicoVinculado.codigo}] ` : ''}{servicoVinculado.nome}
            {servicoVinculado.area && <span style={{ opacity: 0.7 }}> · {servicoVinculado.area}</span>}
            <button onClick={onUnlink} type="button"
              style={{ background: 'none', border: 'none', color: '#0930D0', cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}
              title="Remover vínculo">×</button>
          </span>
        </div>
      )}
      {aberto && (
        <>
          <div onClick={() => setAberto(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2001, marginTop: 2,
            background: '#fff', border: '0.5px solid #0930D0', borderRadius: 5,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            maxHeight: 320, display: 'flex', flexDirection: 'column',
          }}>
            <input autoFocus placeholder="Buscar por nome, código ou área..."
              value={busca} onChange={e => setBusca(e.target.value)}
              style={{ ...inputSm, margin: 6, fontSize: 11 }} />
            <div style={{ overflowY: 'auto', maxHeight: 260 }}>
              {filtrados.length === 0 && (
                <div style={{ padding: 10, fontSize: 11, color: '#aaa', textAlign: 'center' }}>
                  Nenhum serviço encontrado.
                </div>
              )}
              {filtrados.map((s: any) => (
                <button key={s.id} type="button"
                  onClick={() => { onSelecionarServico(s); setAberto(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '5px 10px', fontSize: 11, border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    borderBottom: '0.5px solid #f0f0f0',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F0F1F5')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {s.codigo && <span style={{ color: '#0930D0', fontWeight: 500 }}>[{s.codigo}] </span>}
                  {s.nome}
                  {s.area && <span style={{ color: '#888', marginLeft: 6 }}>· {s.area}</span>}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Toggle reutilizável: checkbox + conteúdo desabilitado quando off
function SecaoToggle({ valor, onChange, label, children }: {
  valor: boolean; onChange: (v: boolean) => void; label: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 8,
        fontSize: 12, fontWeight: 600, color: valor ? '#020D7F' : '#888', cursor: 'pointer',
      }}>
        <input type="checkbox" checked={valor} onChange={e => onChange(e.target.checked)} />
        {label}
      </label>
      <div style={{ opacity: valor ? 1 : 0.4, pointerEvents: valor ? 'auto' : 'none' }}>
        {children}
      </div>
    </div>
  );
}

function Secao({ titulo, cor = '#020D7F', children, colapsavel, defaultAberta = true }: {
  titulo: string;
  cor?: string;
  children: React.ReactNode;
  colapsavel?: boolean;
  defaultAberta?: boolean;
}) {
  const [aberta, setAberta] = useState(defaultAberta);
  const tituloEl = (
    <div style={{
      fontSize: 11, fontWeight: 700, color: cor, textTransform: 'uppercase',
      letterSpacing: '0.06em', marginBottom: 6, paddingBottom: 4,
      borderBottom: `0.5px solid ${cor}33`,
      display: 'flex', alignItems: 'center', gap: 6,
      cursor: colapsavel ? 'pointer' : 'default',
      userSelect: 'none',
    }}
    onClick={colapsavel ? () => setAberta(a => !a) : undefined}>
      {colapsavel && <span style={{ transition: 'transform 0.15s', display: 'inline-block', transform: aberta ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>}
      {titulo}
      {colapsavel && !aberta && (
        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 400, color: '#888', textTransform: 'none' }}>
          (clique pra expandir)
        </span>
      )}
    </div>
  );
  return (
    <div>
      {tituloEl}
      {(!colapsavel || aberta) && children}
    </div>
  );
}

// [#d161bf7d] Wrapper que adapta os campos texto-livre da venda
// (data_inicio, prazo_total, data_conclusao_prevista) pro componente
// estruturado RangePrazo. Salva como ISO yyyy-mm-dd nos próprios campos.
//
// IMPORTANTE: usa setDados direto (não setCampo) pra fazer batch update.
// Chamar 3x setCampo em sequência sofreria stale closure — só o último valor
// persistiria. Aqui consolidamos em UMA chamada setDados.
function RangePrazoVenda({ dados, setDados }: { dados: any; setDados: React.Dispatch<React.SetStateAction<any>> }) {
  const isIso = (s: string | null | undefined) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
  const inicioIso = isIso(dados.data_inicio) ? dados.data_inicio : null;
  const conclusaoIso = isIso(dados.data_conclusao_prevista) ? dados.data_conclusao_prevista : null;
  // [#d161bf7d fix] Se prazo_total foi setado (mesmo que pra string vazia),
  // RESPEITA o valor — não faz fallback pra prazo_entrega (que faria o número
  // "voltar" quando user apaga). Fallback só se nunca foi tocado (undefined/null).
  const prazoNum = (() => {
    if (typeof dados.prazo_total === 'number') return dados.prazo_total;
    if (dados.prazo_total !== undefined && dados.prazo_total !== null) {
      return parsePrazoTexto(dados.prazo_total);
    }
    return parsePrazoTexto(dados.prazo_entrega);
  })();
  return (
    <>
      <RangePrazo
        value={{ inicio: inicioIso, prazo_dias: prazoNum, conclusao: conclusaoIso }}
        prazoTextoSugerido={dados.prazo_entrega || dados.prazo_total || null}
        onChange={(v) => {
          // Batch: 1 setDados consolidando todos os campos. Evita stale closure.
          setDados((prev: any) => ({
            ...prev,
            data_inicio: v.inicio || '',
            prazo_total: v.prazo_dias != null ? String(v.prazo_dias) : '',
            data_conclusao_prevista: v.conclusao || somarDias(v.inicio, v.prazo_dias) || '',
          }));
        }}
        labelInicio="Data de Início"
        labelPrazo="Prazo (dias)"
        labelConclusao="Conclusão Prevista"
      />
      <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>
        Conclusão calculada automaticamente. Se quiser texto livre (ex.: "Após assinatura"),
        ajuste em <em>Prazo / Previsão de entrega</em> da proposta de origem.
      </div>
    </>
  );
}
